/**
 * Verplichte structuur van een softwaremelding (besluit Tom 01-10): een
 * probleem beschrijf je in drie stappen (wat deed je, wat verwachtte je, wat
 * gebeurde er), een wens in twee (wat wil je bereiken, waar in de software).
 * De velden worden samengevoegd tot de bestaande kolom `omschrijving`, zodat
 * niets breekt dat die kolom leest; `stappen` krijgt "wat deed je".
 */
export type MeldingSoort = 'probleem' | 'verzoek';

export interface MeldingVelden {
  deed: string;
  verwachtte: string;
  gebeurde: string;
  doel: string;
  waar: string;
}

export const LEGE_VELDEN: MeldingVelden = {
  deed: '',
  verwachtte: '',
  gebeurde: '',
  doel: '',
  waar: '',
};

export interface VeldDefinitie {
  sleutel: keyof MeldingVelden;
  label: string;
  placeholder: string;
  rijen: number;
}

export const VELD_DEFINITIES: Record<MeldingSoort, VeldDefinitie[]> = {
  probleem: [
    {
      sleutel: 'deed',
      label: 'Wat deed je?',
      placeholder: 'Bijv.: factuur 2026-0123 geopend en op "Goedkeuren" geklikt',
      rijen: 2,
    },
    {
      sleutel: 'verwachtte',
      label: 'Wat verwachtte je?',
      placeholder: 'Bijv.: dat de factuur op "Goedgekeurd" zou springen',
      rijen: 1,
    },
    {
      sleutel: 'gebeurde',
      label: 'Wat gebeurde er?',
      placeholder: 'Bijv.: er gebeurde niets, de knop bleef grijs — ook na opnieuw laden',
      rijen: 2,
    },
  ],
  verzoek: [
    {
      sleutel: 'doel',
      label: 'Wat wil je bereiken?',
      placeholder: 'Bijv.: in één oogopslag zien welke verfbestellingen nog niet geleverd zijn',
      rijen: 2,
    },
    {
      sleutel: 'waar',
      label: 'Waar in de software?',
      placeholder: 'Bijv.: op het projectscherm, tab Werkvoorbereiding, bij de bestellingen',
      rijen: 1,
    },
  ],
};

/** Voorbeeld van een goede melding, als hint boven het formulier. */
export const VOORBEELD_MELDING: Record<MeldingSoort, string> = {
  probleem:
    'Goede melding: "Ik opende factuur 2026-0123 en klikte op Goedkeuren. Ik verwachtte dat hij op Goedgekeurd zou springen. Er gebeurde niets; de knop bleef grijs, ook na opnieuw laden."',
  verzoek:
    'Goede wens: "Ik wil per project zien welke verfbestellingen nog niet geleverd zijn, op het projectscherm bij Werkvoorbereiding, zodat ik niet hoef te bellen met de leverancier."',
};

const KOPJES: Record<keyof MeldingVelden, string> = {
  deed: 'Wat deed je',
  verwachtte: 'Wat verwachtte je',
  gebeurde: 'Wat gebeurde er',
  doel: 'Wat wil je bereiken',
  waar: 'Waar in de software',
};

/** De velden die bij deze soort horen, met waarde (getrimd). */
function veldenVoor(soort: MeldingSoort, velden: MeldingVelden) {
  return VELD_DEFINITIES[soort].map((d) => ({ ...d, waarde: (velden[d.sleutel] ?? '').trim() }));
}

/** Labels van de velden die nog leeg zijn (alle velden zijn verplicht). */
export function ontbrekendeVelden(soort: MeldingSoort, velden: MeldingVelden): string[] {
  return veldenVoor(soort, velden)
    .filter((v) => !v.waarde)
    .map((v) => v.label);
}

export function isFormulierCompleet(soort: MeldingSoort, velden: MeldingVelden): boolean {
  return ontbrekendeVelden(soort, velden).length === 0;
}

/**
 * De velden samengevoegd tot één omschrijving: elk veld op een eigen regel
 * met zijn kopje, lege velden weggelaten. Zo blijft de oude kolom leesbaar
 * voor Tom, de meekijkers en de AI.
 */
export function stelOmschrijvingSamen(soort: MeldingSoort, velden: MeldingVelden): string {
  return veldenVoor(soort, velden)
    .filter((v) => v.waarde)
    .map((v) => `${KOPJES[v.sleutel]}: ${v.waarde}`)
    .join('\n');
}

/** De oude kolom `stappen` ("wat deed je net?") blijft gevuld bij een probleem. */
export function stappenUitVelden(soort: MeldingSoort, velden: MeldingVelden): string | null {
  if (soort !== 'probleem') return null;
  return velden.deed.trim() || null;
}
